import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  declare props: Props;

  constructor(props: Props) {
    super(props);
  }

  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    // Auto-recover from quota exceeded error by clearing non-essential local storage items
    if (
      error?.name === 'QuotaExceededError' ||
      (typeof error?.message === 'string' && (error.message.includes('quota') || error.message.includes('QuotaExceeded')))
    ) {
      try {
        const legacyKeys = [
          'terra_banners_v1', 'terra_banners_v2', 'terra_banners_v3', 'terra_banners_v4', 'terra_banners_v5',
          'terra_banners_v6', 'terra_banners_v7', 'terra_banners_v8', 'terra_banners_v9',
          'pgmart_analytics_v1', 'pgmart_marketing_data', 'pgmart_marketing_data_v2',
          'pgmart_reviews_v2', 'pgmart_blog_posts', 'terra_products', 'terra_settings'
        ];
        legacyKeys.forEach(k => { try { localStorage.removeItem(k); } catch {} });
      } catch {}
    }

    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught Error Boundary catch:", error, errorInfo);

    // Auto-reload on chunk fetch failure (new build deployment)
    const errorMsg = error?.message || '';
    if (
      errorMsg.includes('Failed to fetch dynamically imported module') ||
      errorMsg.includes('dynamically imported module') ||
      errorMsg.includes('Loading chunk')
    ) {
      const lastReload = sessionStorage.getItem('last_chunk_reload');
      const now = Date.now();
      if (!lastReload || now - Number(lastReload) > 10000) {
        sessionStorage.setItem('last_chunk_reload', String(now));
        window.location.reload();
      }
    }
  }

  private handleResetAndReload = () => {
    try {
      const keysToPreserve = ['pgmart_admin_token'];
      const savedAdmin = localStorage.getItem('pgmart_admin_token');
      localStorage.clear();
      if (savedAdmin) {
        localStorage.setItem('pgmart_admin_token', savedAdmin);
      }
    } catch {}
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#FAF7F5] flex flex-col items-center justify-center p-6 text-center font-sans">
          <div className="bg-white p-8 rounded-3xl shadow-xl border border-stone-200 max-w-md w-full space-y-4">
            <div className="w-14 h-14 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto text-2xl font-bold">
              ⚠️
            </div>
            <h2 className="text-xl font-bold text-stone-900">Application Notice</h2>
            <p className="text-xs text-stone-600 leading-relaxed">
              {this.state.error?.message || "A new update or storage reset is required to render this page."}
            </p>
            <div className="pt-2 space-y-2">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="w-full bg-[#C0654B] text-white font-bold py-3 rounded-xl text-xs hover:bg-[#8B4A38] transition-colors cursor-pointer"
              >
                Reload Application
              </button>
              <button
                type="button"
                onClick={this.handleResetAndReload}
                className="w-full bg-stone-100 text-stone-700 font-semibold py-2.5 rounded-xl text-xs hover:bg-stone-200 transition-colors cursor-pointer"
              >
                Clear Cache & Refresh
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

